import { CreateDiscountRequest } from '@/services/billing/types/create-billing.type';
import { Box, Card, Divider, CardHeader, Typography, CardContent } from '@mui/material';
import { ShowIf } from '@utils/index';
import { useMemo } from 'react';
import { useWatch, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useBillingStore } from '../../store/billing.store';
import { useDecimalFormatter } from '@hooks/index';

export const BillingOrderSummary = () => {
  const { t, i18n } = useTranslation();

  const formatter = useDecimalFormatter(i18n.language);

  const selectedMilestones = useBillingStore((state) => state.selectedMilestones);

  const { control } = useFormContext();

  const watchedDiscounts: CreateDiscountRequest[] = useWatch({
    control,
    name: 'discounts',
  });

  const subTotalAmountToBeBilled = useMemo(() => {
    return selectedMilestones.reduce((sum, so) => sum + so.total, 0);
  }, [selectedMilestones]);

  const totalDiscountAmount = useMemo(() => {
    return watchedDiscounts.reduce((sum, discount) => sum + discount.amount, 0);
  }, [watchedDiscounts]);

  const totalAmountToBeBilled = useMemo(() => {
    return subTotalAmountToBeBilled - totalDiscountAmount;
  }, [subTotalAmountToBeBilled, totalDiscountAmount]);

  return (
    <>
      <Divider sx={{ my: 3 }} />
      <Card sx={{ mb: 2 }}>
        <CardHeader title={t('Order Summary')} />
        <CardContent>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="body1" sx={{ mb: 1 }}>
              {t('Subtotal')}: $ {formatter.format(subTotalAmountToBeBilled)}
            </Typography>

            <ShowIf condition={watchedDiscounts.length > 0}>
              <Typography
                variant="body1"
                sx={{ mb: 1, fontWeight: 'medium', color: 'text.secondary' }}
              >
                {t('Total Discounts')}: - $ {formatter.format(totalDiscountAmount)}
              </Typography>
            </ShowIf>

            <Typography
              variant="h6"
              sx={{ fontWeight: 'bold', borderTop: 1, borderColor: 'divider', pt: 1, mb: 2 }}
            >
              {t('Total')}: $ {formatter.format(totalAmountToBeBilled)}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
              {t('Total to be invoiced $ {{Total}}', {
                Total: formatter.format(totalAmountToBeBilled),
              })}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </>
  );
};
